import { find, kebabCase, uniq, max, get, round, multiply } from 'lodash';
import debug from 'debug';
import { CardData, StorySettingsType } from 'types/story';
import { CARD_TYPE, DEFAULT_SCORE } from 'common/constants';
import { CardFacade } from 'common/utils/facades/card-facade';
import { int } from 'common/utils/helpers';
import { lsNames } from 'client/constants/common';
import * as cssMQ from './media-queries';

export { updateStoryLastCardId, getStoryLastCardId } from './history-storage';

export { cssMQ };
export const attrSelector = selector => `[data-${kebabCase(selector)}]`;

const log = debug('client:utils:AnswersStorage');

type StoredDataT = Record<
	'cards',
	Record<
		string, // card id
		string[] | undefined // answer id arr
	>
>;

export class AnswersStorage {
	storyId: string;

	cardId: string;

	isMultiple: boolean;

	itemName: string;

	/**
	 * @param storyId {String}
	 * @param cardId {String}
	 * @param isMultiple {Boolean} is allowed to select multiple answers
	 */
	constructor({ storyId = '', cardId = '', isMultiple = false }) {
		this.storyId = storyId;
		this.cardId = cardId;
		this.isMultiple = isMultiple;
		this.itemName = lsNames.answersStorage(storyId);
	}

	get storedData(): StoredDataT {
		const data = localStorage.getItem(this.itemName);
		const noData = JSON.stringify({ cards: {} });
		return JSON.parse(data || noData);
	}

	add(answerId: string) {
		const prevAnswers = this.storedData.cards[this.cardId] ?? [];
		const answers = this.isMultiple ? uniq([...prevAnswers, answerId]) : [answerId];

		const nextData = {
			...this.storedData,
			cards: {
				...this.storedData.cards,
				[this.cardId]: answers,
			},
		};

		localStorage.setItem(this.itemName, JSON.stringify(nextData));
	}

	/**
	 * Get score for played cards
	 * @param storySettings
	 * @return {number} relative score
	 */
	totalResults(storySettings: StorySettingsType, cards: CardData[]) {
		const RESULT = {
			scoreMax: 0, // Maximum score - the sum of the maximum score for the cards played
			scoreTotal: 0, // Total score - the amount of score received for the cards played
			scoreRelative: 0, // Relative score - the ratio of the total score to the maximum (0 - 100)
			answersTotal: 0,
			answersCorrect: 0,
		};

		Object.entries(this.storedData.cards).forEach(([cardId, chosenAnswerIds = []]) => {
			const { score, answers = {} } = storySettings.cards?.[cardId] ?? {};
			const cardData = find(cards, ['_id', cardId]);
			const canScore = cardData ? new CardFacade(cardData).hasFeature.score : false;
			const defaultCardScore = canScore ? DEFAULT_SCORE : 0;

			// Get card score
			const cardScore = int(score ?? defaultCardScore);
			if (cardScore) RESULT.scoreMax += cardScore;

			// Get max answer score per card (eg. PersonalityTest card)
			const defaultAnswerScore = cardData?.type === CARD_TYPE.PERSONALITY_TEST ? DEFAULT_SCORE : 0;
			const answerMaxScore = max(Object.values(answers).map(answer => int(answer.score ?? defaultAnswerScore)));
			if (answerMaxScore) RESULT.scoreMax += answerMaxScore;

			// sum up the score for chosen answers
			chosenAnswerIds.forEach(answerId => {
				// Add card score for a correct answer (eg. Trivia card)
				// TODO: if isMultiple score should be incremented partially in depends on count of correct answers
				const isCorrect = get(answers, `${answerId}.isCorrect`);
				RESULT.scoreTotal += isCorrect ? cardScore : 0;

				// inc counters
				if (canScore) {
					RESULT.answersTotal += 1;
					if (isCorrect) RESULT.answersCorrect += 1;
				}

				// Add answer score (PersonalityTest card)
				const answerScore = int(answers?.[answerId]?.score ?? defaultAnswerScore);
				if (answerScore) RESULT.scoreTotal += answerScore;
			});
		});

		RESULT.scoreRelative = multiply(RESULT.scoreTotal / RESULT.scoreMax || 0, 100);

		log('result', RESULT);

		return RESULT;
	}
}

class _AnswerDelta {
	private delta: Record<string, number> = {};

	start(cardId: string) {
		this.delta[cardId] = Date.now();

		log('_AnswerDelta start', this.delta[cardId]);
	}

	end(cardId: string) {
		if (!this.delta[cardId]) return;

		this.delta[cardId] = round((Date.now() - this.delta[cardId]) / 1000, 3);

		log('_AnswerDelta end', this.delta[cardId]);
	}

	get(cardId: string) {
		return this.delta[cardId];
	}
}

export const AnswerDelta = new _AnswerDelta();
